import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CompetitionArena = ({ competitions, leaderboard, onJoin }) => {
  const [activeTab, setActiveTab] = useState('active');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-success bg-success/10 border-success/20';
      case 'Upcoming': return 'text-warning bg-warning/10 border-warning/20';
      case 'Ended': return 'text-muted-foreground bg-muted/10 border-muted/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-success';
      case 'Intermediate': return 'text-warning';
      case 'Advanced': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const filteredCompetitions = competitions.filter(comp => {
    if (activeTab === 'active') return comp.status === 'Active';
    if (activeTab === 'upcoming') return comp.status === 'Upcoming';
    return comp.status === 'Ended';
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Competition Arena</h2>
          <p className="text-muted-foreground">Challenge yourself and compete with the community</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Trophy" size={20} className="text-warning" />
          <span className="text-sm text-muted-foreground">Total Prize Pool: $50,000</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-muted/20 p-1 rounded-lg">
        {[
          { id: 'active', label: 'Active', count: competitions.filter(c => c.status === 'Active').length },
          { id: 'upcoming', label: 'Upcoming', count: competitions.filter(c => c.status === 'Upcoming').length },
          { id: 'ended', label: 'Ended', count: competitions.filter(c => c.status === 'Ended').length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
              activeTab === tab.id
                ? 'bg-accent text-white shadow-glow'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Competitions List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredCompetitions.map((competition) => (
            <div key={competition.id} className="holographic-card">
              <div className="p-6">
                {/* Competition Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{competition.title}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(competition.status)}`}>
                        {competition.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{competition.description}</p>
                    
                    {/* Competition Details */}
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} className="text-muted-foreground" />
                        <span className="text-muted-foreground">Ends: {competition.endDate}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Users" size={14} className="text-muted-foreground" />
                        <span className="text-muted-foreground">{competition.participants} participants</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="BarChart3" size={14} className={getDifficultyColor(competition.difficulty)} />
                        <span className={getDifficultyColor(competition.difficulty)}>{competition.difficulty}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-2xl font-bold text-warning mb-1">{competition.prize}</div>
                    <div className="text-xs text-muted-foreground">Prize Pool</div>
                  </div>
                </div>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {competition.technologies.map((tech, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-muted/30 text-xs text-muted-foreground rounded-md"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Time Remaining</span>
                    <span className="text-foreground font-medium">{competition.timeRemaining}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-accent to-cyber-purple"
                      style={{ width: `${competition.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex -space-x-2">
                      {competition.topParticipants.map((participant, index) => (
                        <div key={index} className="w-8 h-8 rounded-full overflow-hidden border-2 border-card">
                          <Image
                            src={participant.avatar}
                            alt={participant.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">Top participants</span>
                  </div>

                  <Button
                    variant={competition.joined ? "outline" : "default"}
                    size="sm"
                    iconName={competition.joined ? "CheckCircle" : "Zap"}
                    iconPosition="left"
                    onClick={() => onJoin(competition.id)}
                    className={!competition.joined ? "morphing-cta" : ""}
                  >
                    {competition.joined ? "Joined" : "Join Competition"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Leaderboard */}
        <div className="space-y-6">
          <div className="holographic-card">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Global Leaderboard</h3>
                <Icon name="Trophy" size={20} className="text-warning" />
              </div>

              <div className="space-y-3">
                {leaderboard.map((user, index) => (
                  <div key={user.id} className="flex items-center space-x-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors duration-300">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-warning text-white' :
                      index === 1 ? 'bg-muted-foreground text-white' :
                      index === 2 ? 'bg-warning/70 text-white': 'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <Image
                        src={user.avatar}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{user.name}</div>
                      <div className="text-xs text-muted-foreground">{user.title}</div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-bold text-accent">{user.points}</div>
                      <div className="text-xs text-muted-foreground">points</div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="BarChart3"
                iconPosition="left"
                className="mt-4"
              >
                View Full Rankings
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="holographic-card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Your Stats</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Competitions Won</span>
                  <span className="font-bold text-success">3</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Current Rank</span>
                  <span className="font-bold text-accent">#47</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Points</span>
                  <span className="font-bold text-warning">2,450</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Win Rate</span>
                  <span className="font-bold text-cyber-purple">68%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionArena;